import dayjs, { Dayjs } from 'dayjs';
import { useFormik } from 'formik';
import { capitalize, pick } from 'lodash';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { findDefaultValue } from '@/modules/common/utils/default-value.ts';
import { fileToBase64 } from '@/modules/common/utils/file.ts';
import {
  AUS_CARD_NUMBER_REGEX,
  AUS_MEDICARE_CARD_NUMBER,
  getPassportNumberExample,
  isValidDriverLicenseNumber,
  isValidPassportNumber,
} from '@/modules/common/utils/regex.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useDeleteEntityDocumentMutation,
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { auStateOptions } from '@/modules/entity/static/au-states.ts';
import { countryOptions } from '@/modules/entity/static/countries.ts';
import { medicareCardColorOptions } from '@/modules/entity/static/medicare-card-color.ts';
import { ChangeShareholderComponentProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import {
  CalendarInput,
  Dropzone,
  Select,
  SelectButtonOption,
  SelectButtons,
  TextInputField,
} from 'ui';
import { fixNameEnum } from 'ui/utils';

export enum PersonIdentificationDocType {
  DriverLicense = 'driver_license',
  Passport = 'passport',
  Medicare = 'medicare',
}

export enum PersonIdentificationDocFullType {
  DriverLicenseFront = 'driver_license_front',
  DriverLicenseBack = 'driver_license_back',
  Passport = 'passport',
  Medicare = 'medicare',
}

interface Document {
  data: string;
  type: string;
  index: string;
}

export interface IdentificationDocumentsFormik {
  documents: Document[];
  driver_license_number: string;
  driver_license_card_number: string;
  drivers_license_state: string;
  passport_number: string;
  passport_document_expiry: string;
  country_of_issue: string;
  medicare_card_color: string;
  medicare_card_number: string;
  medicare_individual_reference_number: string;
  medicare_name_on_card: string;
  medicare_expire_date: string;
}

export const validationSchemaDocuments = yup.object({
  documents: yup.array().of(
    yup
      .object({
        data: yup.string().trim().required('This field is required'),
        type: yup.string().trim().required('This field is required'),
        index: yup.string().trim().required('This field is required'),
      })
      .required('Please upload document'),
  ),
  // .min(1, 'Please upload document')
  // .required('Please upload document'),
});

export const validationSchemasPersonIdentificationDocuments = {
  [PersonIdentificationDocType.DriverLicense]: yup.object({
    driver_license_number: yup
      .string()
      .trim()
      .when(['documents', 'country_of_issue'], ([documents, countryOfIssue], schema) => {
        if (
          documents?.some((item: Document) =>
            item.type?.includes(PersonIdentificationDocType.DriverLicense),
          )
        ) {
          return schema
            .test(
              'driver-license-number',
              'Invalid driver licence number',
              value => !value || isValidDriverLicenseNumber(countryOfIssue || 'AUS', value),
            )
            .required('This field is required');
        }

        return schema;
      }),

    driver_license_card_number: yup
      .string()
      .trim()
      .when(['documents', 'country_of_issue'], ([documents, countryOfIssue], schema) => {
        if (
          documents?.some((item: Document) =>
            item.type?.includes(PersonIdentificationDocType.DriverLicense),
          )
        ) {
          return schema
            .test(
              'driver-license-card-number',
              'Invalid driver licence card number',
              value =>
                !value ||
                (countryOfIssue === 'NZL'
                  ? /^[A-Z0-9]{3}$/i.test(value)
                  : AUS_CARD_NUMBER_REGEX.test(value)),
            )
            .required('This field is required');
        }

        return schema;
      }),
    drivers_license_state: yup
      .string()
      .trim()
      .when(['documents', 'country_of_issue'], ([documents, countryOfIssue], schema) => {
        if (
          countryOfIssue !== 'NZL' &&
          documents?.some((item: Document) =>
            item.type?.includes(PersonIdentificationDocType.DriverLicense),
          )
        ) {
          return schema.required('This field is required');
        }

        return schema;
      }),
  }),
  [PersonIdentificationDocType.Passport]: yup.object({
    country_of_issue: yup
      .string()
      .trim()
      .when('documents', ([documents], schema) => {
        if (
          documents?.some((item: Document) => item.type === PersonIdentificationDocType.Passport)
        ) {
          return schema.required('This field is required');
        }

        return schema;
      }),
    passport_number: yup
      .string()
      .trim()
      .when('documents', ([documents], schema) => {
        if (
          documents?.some((item: Document) => item.type === PersonIdentificationDocType.Passport)
        ) {
          return schema
            .when('country_of_issue', (country, schema) => {
              return schema.test(
                'passport-number',
                'Invalid passport number',
                value => !value || isValidPassportNumber(country[0], value),
              );
            })
            .required('This field is required');
        }

        return schema;
      }),
    passport_document_expiry: yup
      .string()
      .trim()
      .when('documents', ([documents], schema) => {
        if (
          documents?.some((item: Document) => item.type === PersonIdentificationDocType.Passport)
        ) {
          return schema.when('country_of_issue', ([country], schema) => {
            if (country === 'NZL') {
              return schema.required('This field is required');
            }
            // Return the scheme without changes if the country is not AUS or UKR
            return schema;
          });
        }

        return schema;
      }),
  }),
  [PersonIdentificationDocType.Medicare]: yup.object({
    medicare_card_color: yup
      .string()
      .trim()
      .when('documents', ([documents], schema) => {
        if (
          documents?.some((item: Document) => item.type === PersonIdentificationDocType.Medicare)
        ) {
          return schema.required('This field is required');
        }

        return schema;
      }),
    medicare_card_number: yup
      .string()
      .trim()
      .when('documents', ([documents], schema) => {
        if (
          documents?.some((item: Document) => item.type === PersonIdentificationDocType.Medicare)
        ) {
          return schema
            .matches(
              AUS_MEDICARE_CARD_NUMBER,
              'Incorrect medicare card number, example: 1234 56789 0',
            )
            .required('This field is required');
        }

        return schema;
      }),
    medicare_individual_reference_number: yup.number().when('documents', ([documents], schema) => {
      if (documents?.some((item: Document) => item.type === PersonIdentificationDocType.Medicare)) {
        return schema
          .integer('Must include only numbers')
          .typeError('Must be a number')
          .min(1, 'Individual Reference Number must be between 1 and 99')
          .max(99, 'Individual Reference Number must be between 1 and 99')
          .required('This field is required');
      }

      return schema;
    }),
    medicare_name_on_card: yup
      .string()
      .trim()
      .when('documents', ([documents], schema) => {
        if (
          documents?.some((item: Document) => item.type === PersonIdentificationDocType.Medicare)
        ) {
          return schema.required('This field is required');
        }

        return schema;
      }),
    medicare_expire_date: yup
      .string()
      .trim()
      .when('documents', ([documents], schema) => {
        if (
          documents?.some((item: Document) => item.type === PersonIdentificationDocType.Medicare)
        ) {
          return schema.required('This field is required');
        }

        return schema;
      }),
  }),
};
//
const initialValues: IdentificationDocumentsFormik = {
  documents: [],
  driver_license_number: '',
  driver_license_card_number: '',
  drivers_license_state: '',
  passport_number: '',
  passport_document_expiry: '',
  country_of_issue: '',
  medicare_card_color: '',
  medicare_card_number: '',
  medicare_individual_reference_number: '',
  medicare_name_on_card: '',
  medicare_expire_date: '',
};

export const mapFieldGroupsDoc: Record<
  PersonIdentificationDocType,
  (keyof IdentificationDocumentsFormik)[]
> = {
  [PersonIdentificationDocType.Passport]: [
    // 'country_of_issue',
    'passport_number',
    'passport_document_expiry',
  ],
  [PersonIdentificationDocType.DriverLicense]: [
    'driver_license_card_number',
    'driver_license_number',
    'drivers_license_state',
  ],
  [PersonIdentificationDocType.Medicare]: [
    'medicare_expire_date',
    'medicare_card_color',
    'medicare_card_number',
    'medicare_name_on_card',
    'medicare_individual_reference_number',
  ],
};

export const PersonIdentificationDocuments = ({
  index,
  title,
  entity_type_prefix,
  person_details_key,
  moveForward,
  allowSkip,
  titleHint,
}: ChangeShareholderComponentProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));
  const { mutateAsync: deleteDocument, isLoading: isDocumentDeletion } =
    useDeleteEntityDocumentMutation();
  const [choseTypeDoc, setChoseTypeDoc] = useState('');
  const [docType, setDocType] = useState<PersonIdentificationDocType>(
    PersonIdentificationDocType.Passport,
  );
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [commonError, setCommonError] = useState('');
  const [commonErrorTypeDoc, setCommonErrorTypeDoc] = useState<
    PersonIdentificationDocType | undefined
  >();
  const [docsUrls, setDocsUrls] = useState({
    [PersonIdentificationDocType.Passport]: '',
    [PersonIdentificationDocType.Medicare]: '',
    [`${PersonIdentificationDocType.DriverLicense}_front`]: '',
    [`${PersonIdentificationDocType.DriverLicense}_back`]: '',
  });
  const entityTypeDetails = useMemo(
    () => get(data?.entity, entity_type_prefix, {}),
    [data, entity_type_prefix],
  );

  const documentOptions = useMemo<SelectButtonOption<PersonIdentificationDocType>[]>(
    () => [
      {
        label: 'Driver licence',
        value: PersonIdentificationDocType.DriverLicense,
        isError: commonErrorTypeDoc === PersonIdentificationDocType.DriverLicense,
      },
      {
        label: 'Passport',
        value: PersonIdentificationDocType.Passport,
        isError: commonErrorTypeDoc === PersonIdentificationDocType.Passport,
      },
      {
        label: 'Medicare',
        value: PersonIdentificationDocType.Medicare,
        isError: commonErrorTypeDoc === PersonIdentificationDocType.Medicare,
      },
    ],
    [commonErrorTypeDoc],
  );

  const personalDetails = useMemo(
    () =>
      person_details_key && index !== undefined
        ? get<any>(entityTypeDetails, `${person_details_key}.${index}`, {})
        : entityTypeDetails,
    [entityTypeDetails, index, person_details_key],
  );
  const personDocumentIndex = personalDetails?.index || '';
  const personalCompoundDocumentIndex = `${person_details_key}_${personDocumentIndex}`;
  const generateInitialValues = (): IdentificationDocumentsFormik => {
    if (person_details_key && (index || index === 0)) {
      const personDetails = personalDetails as IdentificationDocumentsFormik & { country?: string };

      return {
        ...initialValues,
        ...personDetails,
        country_of_issue:
          personDetails?.country ||
          (personDetails?.country_of_issue !== undefined && personDetails?.country_of_issue) ||
          entityTypeDetails?.country ||
          '',
      };
    }

    return {
      ...initialValues,
      ...entityTypeDetails,
      country_of_issue:
        (entityTypeDetails?.country_of_issue !== undefined &&
          entityTypeDetails?.country_of_issue) ||
        entityTypeDetails?.country ||
        '',
    };
  };

  const generatePayload = useCallback(
    (values: IdentificationDocumentsFormik) => {
      const { documents, ...value } = values;
      if (person_details_key && (index || index === 0)) {
        return set(
          {
            ...data?.entity,
            documents: documents?.filter(item => !item.data?.toLowerCase()?.startsWith('http')),
          },
          entity_type_prefix,
          set(
            entityTypeDetails,
            person_details_key,
            get(entityTypeDetails, person_details_key || '', [])?.map(
              (shareholder: any, indexOfShareholder: number) =>
                indexOfShareholder === index ? { ...shareholder, ...value } : shareholder,
            ),
          ),
        );
      }
      return set(
        {
          ...data?.entity,
          documents: documents?.filter(item => !item.data?.toLowerCase()?.startsWith('http')),
        },
        entity_type_prefix,
        {
          ...entityTypeDetails,
          ...value,
        },
      );
    },
    [data?.entity, entityTypeDetails, entity_type_prefix, index, person_details_key],
  );

  const currentDocuments = useMemo(() => {
    const documents = (data?.entity?.document_files || []).filter(
      item =>
        (item.type === PersonIdentificationDocType.Passport ||
          item.type?.includes(PersonIdentificationDocType.DriverLicense) ||
          item.type === PersonIdentificationDocType.Medicare) &&
        item.index === personalCompoundDocumentIndex,
    );

    if (documents?.[0]?.type) {
      if (documents?.[0]?.type?.includes(PersonIdentificationDocType.DriverLicense)) {
        setDocType(PersonIdentificationDocType.DriverLicense);
      } else {
        setDocType(documents?.[0]?.type as PersonIdentificationDocType);
      }
    }

    return documents;
  }, [data?.entity?.document_files, personalCompoundDocumentIndex]);

  const formik = useFormik<IdentificationDocumentsFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemasPersonIdentificationDocuments?.[docType]?.fields
      ? validationSchemaDocuments.shape(
          validationSchemasPersonIdentificationDocuments[docType].fields,
        )
      : undefined,
    initialValues: generateInitialValues(),
    onSubmit: async (values, { setFieldError }) => {
      setCommonError('');
      setCommonErrorTypeDoc(undefined);
      if (currentDocuments.length < 1 && values.documents.length < 1) {
        setFieldError('documents', 'Please upload document');
        return;
      }
      const initInfo = pick<IdentificationDocumentsFormik, keyof IdentificationDocumentsFormik>(
        values,
        [
          'country_of_issue',
          'passport_number',
          'passport_document_expiry',
          'driver_license_card_number',
          'driver_license_number',
          'drivers_license_state',
          'medicare_expire_date',
          'medicare_card_color',
          'medicare_card_number',
          'medicare_name_on_card',
          'medicare_individual_reference_number',
        ],
      );

      const uploadedTypes = [...currentDocuments, ...values.documents].reduce<
        Set<PersonIdentificationDocType>
      >((acc, doc) => {
        switch (doc.type) {
          case PersonIdentificationDocFullType.Passport:
            acc.add(PersonIdentificationDocType.Passport);
            break;
          case PersonIdentificationDocFullType.DriverLicenseFront:
          case PersonIdentificationDocFullType.DriverLicenseBack:
            acc.add(PersonIdentificationDocType.DriverLicense);
            break;
          case PersonIdentificationDocFullType.Medicare:
            acc.add(PersonIdentificationDocType.Medicare);
            break;
        }
        return acc;
      }, new Set());

      for (const docType of Object.keys(mapFieldGroupsDoc) as PersonIdentificationDocType[]) {
        let fields = mapFieldGroupsDoc[docType];

        //
        // SPECIAL CASE: Passport — require expiry ONLY if country_of_issue === 'NZL'
        //
        if (docType === PersonIdentificationDocType.Passport) {
          const isNZL = values.country_of_issue === 'NZL';

          if (!isNZL) {
            // Remove expiry because it is NOT required for non-NZL passports
            fields = fields.filter(f => f !== 'passport_document_expiry');
          }
        }

        // Fields that the user filled in
        const filledFields = fields.filter(f => !!values[f]);

        // Are there any documents of this type uploaded?
        const hasUploadedDoc = uploadedTypes.has(docType);

        const areAllFieldsFilled = filledFields.length === fields.length;
        const areSomeFieldsFilled = filledFields.length > 0;

        //
        // CASE 1 - documents are uploaded, but the fields are NOT completely filled in
        //
        if (hasUploadedDoc && !areAllFieldsFilled) {
          const missingFields = fields.filter(f => !values[f]);
          const formattedMissing = missingFields.map(item => fixNameEnum(item)).join(', ');

          setCommonErrorTypeDoc(docType);
          setCommonError(
            `Please fill all fields for ${docType.replace(
              '_',
              ' ',
            )}: ${formattedMissing} or remove the uploaded documents for ${docType.replace(
              '_',
              ' ',
            )}`,
          );
          return;
        }

        //
        // CASE 2 - fields are filled in, but documents are NOT uploaded
        //
        if (!hasUploadedDoc && areSomeFieldsFilled) {
          const formattedFields = filledFields.map(item => fixNameEnum(item)).join(', ');

          setCommonErrorTypeDoc(docType);
          setCommonError(
            `Please upload document for ${docType.replace(
              '_',
              ' ',
            )} or remove information from fields: ${formattedFields}`,
          );
          return;
        }

        // CASE 3 — either completely filled and loaded, or completely empty → everything is ok
      }

      let personDetails: IdentificationDocumentsFormik | null = null;
      if (person_details_key && (index || index === 0)) {
        personDetails = get(entityTypeDetails, person_details_key, [])?.find(
          (_: any, indexOfPerson: number) => indexOfPerson === index,
        ) as IdentificationDocumentsFormik;
      }
      const currentInfo = pick<IdentificationDocumentsFormik, keyof IdentificationDocumentsFormik>(
        personDetails || entityTypeDetails,
        [
          'country_of_issue',
          'passport_number',
          'passport_document_expiry',
          'driver_license_card_number',
          'driver_license_number',
          'drivers_license_state',
          'medicare_expire_date',
          'medicare_card_color',
          'medicare_card_number',
          'medicare_name_on_card',
          'medicare_individual_reference_number',
        ],
      );
      const initDoc =
        values?.documents?.filter(item => !item.data?.toLowerCase()?.startsWith('http')) ?? [];

      if (!isEqual(initInfo, currentInfo) || initDoc?.length > 0) {
        await updateEntity(generatePayload(values));
      }
      moveForward && moveForward();
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    setCommonError('');
    setCommonErrorTypeDoc(undefined);
  }, [formik.values]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  useEffect(() => {
    formik.setFieldValue(
      'documents',
      currentDocuments.map<Document>(item => ({
        data: item.url as string,
        index: item.index as string,
        type: item.type as string,
      })),
    );
  }, [currentDocuments]);

  const handleChangeType = (docType: PersonIdentificationDocType) => {
    setDocType(docType);
    formik.setFieldError('documents', '');
  };

  const onFileAccepted = async (type: string, file: File) => {
    setChoseTypeDoc(type);
    setIsFileLoading(true);

    const base64 = await fileToBase64(file, true);

    formik.setFieldValue('documents', [
      ...(formik.values.documents || []),
      {
        type,
        data: base64,
        index: personalCompoundDocumentIndex,
      },
    ]);
    formik.setFieldError('documents', '');
    formik.setErrors({});
    setIsFileLoading(false);
    setChoseTypeDoc('');
  };

  const onFileClear = async (type: string) => {
    setChoseTypeDoc(type);
    const current = (formik.values.documents || [])?.find(doc => doc.type === type);
    if (current?.data?.toLowerCase()?.startsWith('http')) {
      await deleteDocument({
        type,
        index: personalCompoundDocumentIndex,
        entity_id: data?.entity.id || 0,
      });
    }
    await formik.setFieldValue(
      'documents',
      (formik.values.documents || [])?.filter(doc => doc.type !== type),
    );
    formik.setFieldError('documents', '');
    formik.setErrors({});

    setDocsUrls(urls => ({ ...urls, [type]: '' }));
    setChoseTypeDoc('');
  };

  const handleChangeSelect = (field: string, value: string) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
    if (field === 'country_of_issue') {
      formik.setFieldValue('passport_document_expiry', '');
      formik.setFieldError('passport_document_expiry', '');
    }
  };

  useEffect(() => {
    if (data && data.entity.document_files) {
      const documentPassport = data.entity.document_files.find(
        ({ type, index }) =>
          type === PersonIdentificationDocType.Passport && index === personalCompoundDocumentIndex,
      );
      const documentMedicare = data.entity.document_files.find(
        ({ type, index }) =>
          type === PersonIdentificationDocType.Medicare && index === personalCompoundDocumentIndex,
      );
      const documentDriverLicenseFront = data.entity.document_files.find(
        ({ type, index }) =>
          type === `${PersonIdentificationDocType.DriverLicense}_front` &&
          index === personalCompoundDocumentIndex,
      );
      const documentDriverLicenseBack = data.entity.document_files.find(
        ({ type, index }) =>
          type === `${PersonIdentificationDocType.DriverLicense}_back` &&
          index === personalCompoundDocumentIndex,
      );
      setDocsUrls({
        [PersonIdentificationDocType.Passport]: documentPassport?.url || '',
        [PersonIdentificationDocType.Medicare]: documentMedicare?.url || '',
        [`${PersonIdentificationDocType.DriverLicense}_front`]:
          documentDriverLicenseFront?.url || '',
        [`${PersonIdentificationDocType.DriverLicense}_back`]: documentDriverLicenseBack?.url || '',
      });
    }
  }, [data?.entity?.document_files, personalCompoundDocumentIndex]);

  const handleChangeDate = (fieldName: string) => (date: dayjs.Dayjs) => {
    formik.setFieldValue(fieldName, date ? date.format('YYYY-MM-DD').toString() : '');
    formik.setFieldError(fieldName, '');
  };
  const getDisabledDate = (date: Dayjs) => date.isBefore() && !date.isSame(dayjs(), 'day');

  return (
    <OnboardingWrapper
      title={
        title ||
        `${
          person_details_key
            ? capitalize(
                person_details_key?.endsWith('s')
                  ? person_details_key?.slice(0, -1)
                  : person_details_key,
              )
            : ''
        } ${person_details_key ? (index || index === 0) && '#' + (index + 1) : ''} ${
          personalDetails
            ? (personalDetails?.first_name ?? '') + ' ' + (personalDetails?.last_name ?? '')
            : ''
        } ${personalDetails?.type === 'company' ? 'Company Extract' : 'Identification documents'}`
      }
      titleHint={titleHint}
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      isLoading={isLoading || isFileLoading}
    >
      <div className="w-full flex flex-col space-y-4">
        <p className="text-[24px] leading-[36px]">
          Please upload one or more documents (certified copies required)
        </p>
        <SelectButtons
          options={documentOptions}
          selected={
            findDefaultValue(
              documentOptions,
              docType,
            ) as SelectButtonOption<PersonIdentificationDocType>
          }
          onChange={handleChangeType}
        />
        {docType === PersonIdentificationDocType.DriverLicense && (
          <>
            <TextInputField name="driver_license_number" label="drivers licence Number" />
            <TextInputField name="driver_license_card_number" label="drivers licence Card Number" />
            {formik.values.country_of_issue !== 'NZL' && (
              <Select
                isClearable
                label="drivers licence state"
                options={auStateOptions}
                onChange={value => handleChangeSelect('drivers_license_state', value || '')}
                value={formik.values.drivers_license_state}
                error={formik.errors.drivers_license_state}
              />
            )}
            <div className="w-full flex flex-col space-y-1">
              <p className="text-[14px] leading-[22px]">Image Front</p>
              <p className="text-3 leading-5">JPG, PNG or PDF. Up to 10 mb</p>
            </div>
            <Dropzone
              onFileAccepted={onFileAccepted.bind(
                null,
                `${PersonIdentificationDocType.DriverLicense}_front`,
              )}
              onFileClear={onFileClear.bind(
                null,
                `${PersonIdentificationDocType.DriverLicense}_front`,
              )}
              value={
                docsUrls[PersonIdentificationDocType.DriverLicense + '_front'] ||
                (formik.values.documents || [])?.find(
                  ({ type }) => PersonIdentificationDocType.DriverLicense + '_front' === type,
                )?.data
              }
              error={formik.errors.documents as string}
              loading={
                (isFileLoading || isDocumentDeletion) &&
                choseTypeDoc === PersonIdentificationDocType.DriverLicense + '_front'
              }
            />
            <div className="w-full flex flex-col space-y-1">
              <p className="text-[14px] leading-[22px]">Image Back</p>
              <p className="text-3 leading-5">JPG, PNG or PDF. Up to 10 mb</p>
            </div>
            <Dropzone
              onFileAccepted={onFileAccepted.bind(
                null,
                `${PersonIdentificationDocType.DriverLicense}_back`,
              )}
              onFileClear={onFileClear.bind(
                null,
                `${PersonIdentificationDocType.DriverLicense}_back`,
              )}
              value={
                docsUrls[PersonIdentificationDocType.DriverLicense + '_back'] ||
                (formik.values.documents || [])?.find(
                  ({ type }) => PersonIdentificationDocType.DriverLicense + '_back' === type,
                )?.data
              }
              error={formik.errors.documents as string}
              loading={
                (isFileLoading || isDocumentDeletion) &&
                choseTypeDoc === PersonIdentificationDocType.DriverLicense + '_back'
              }
            />
          </>
        )}
        {docType === PersonIdentificationDocType.Passport && (
          <>
            <TextInputField
              name="passport_number"
              label="passport number"
              placeholder={getPassportNumberExample(formik.values.country_of_issue)}
            />
            <Select
              isClearable
              options={countryOptions}
              label="country of issue"
              onChange={value => handleChangeSelect('country_of_issue', value || '')}
              value={formik.values.country_of_issue}
              error={formik.errors.country_of_issue}
            />
            {formik.values.country_of_issue === 'NZL' && (
              <CalendarInput
                label="Passport document expiry"
                error={formik.errors.passport_document_expiry}
                defaultValue={
                  formik.values.passport_document_expiry
                    ? dayjs(formik.values.passport_document_expiry)
                    : undefined
                }
                onChange={handleChangeDate('passport_document_expiry')}
                disabledDate={getDisabledDate}
              />
            )}
            <div className="w-full flex flex-col space-y-1">
              <p className="text-[14px] leading-[22px]">Image</p>
              <p className="text-3 leading-5">JPG, PNG or PDF. Up to 10 mb</p>
            </div>
            <Dropzone
              onFileAccepted={onFileAccepted.bind(null, PersonIdentificationDocType.Passport)}
              onFileClear={onFileClear.bind(null, PersonIdentificationDocType.Passport)}
              value={
                docsUrls[PersonIdentificationDocType.Passport] ||
                (formik.values.documents || [])?.find(
                  ({ type }) => PersonIdentificationDocType.Passport === type,
                )?.data
              }
              error={formik.errors.documents as string}
              loading={
                (isFileLoading || isDocumentDeletion) &&
                choseTypeDoc === PersonIdentificationDocType.Passport
              }
            />
          </>
        )}
        {docType === PersonIdentificationDocType.Medicare && (
          <>
            <Select
              isClearable
              options={medicareCardColorOptions}
              label="medicare card color"
              onChange={value => handleChangeSelect('medicare_card_color', value || '')}
              value={formik.values.medicare_card_color}
              error={formik.errors.medicare_card_color}
            />
            <TextInputField name="medicare_card_number" label="medicare card number" />
            <TextInputField
              name="medicare_individual_reference_number"
              label="medicare individual reference number"
            />
            <TextInputField name="medicare_name_on_card" label="medicare name on card" />
            <CalendarInput
              label="medicare expire date"
              defaultValue={
                formik.values.medicare_expire_date
                  ? dayjs(formik.values.medicare_expire_date)
                  : undefined
              }
              onChange={handleChangeDate('medicare_expire_date')}
              error={formik.errors.medicare_expire_date}
            />
            <div className="w-full flex flex-col space-y-1">
              <p className="text-[14px] leading-[22px]">Image</p>
              <p className="text-3 leading-5">JPG, PNG or PDF. Up to 10 mb</p>
            </div>
            <Dropzone
              onFileAccepted={onFileAccepted.bind(null, PersonIdentificationDocType.Medicare)}
              onFileClear={onFileClear.bind(null, PersonIdentificationDocType.Medicare)}
              value={
                docsUrls[PersonIdentificationDocType.Medicare] ||
                (formik.values.documents || [])?.find(
                  ({ type }) => PersonIdentificationDocType.Medicare === type,
                )?.data
              }
              error={formik.errors.documents as string}
              loading={
                (isFileLoading || isDocumentDeletion) &&
                choseTypeDoc === PersonIdentificationDocType.Medicare
              }
            />
          </>
        )}
        {commonError && <span className="text-error-red text-sm">{commonError}</span>}
      </div>
    </OnboardingWrapper>
  );
};
